"use client";
import React, { useState } from "react";
import { InfoTextImageSliderElementType } from "./InfoTextImageSlider";
import styles from "./InfoTextImageSlider.module.scss";
import { MediaItem } from "@/components/MediaItem/MediaItem";
import InfoTextBox from "./InfoTextBox/InfoTextBox";
import { BreakpointValues } from "@/components/_primitives/utils/breakpoints";
import CustomSVG from "@/components/_primitives/CustomSVG/CustomSVG.client";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";

// Strongly types the output object keys as CSS variables
type ItemWidthCSS = {
  [K in keyof BreakpointValues as `--${K}-width-active`]?: string;
};

function calculateItemWidthsCSS(itemHeight: BreakpointValues) {
  const aspectRatios: Record<keyof BreakpointValues, number> = {
    mobile: 1 / 1,
    tablet: 4 / 3,
    desktop: 3 / 2,
    widescreen: 3 / 2,
    fullhd: 3 / 2,
    ultrahd: 3 / 2,
  };

  const cssVars: ItemWidthCSS = {};

  for (const breakpoint in itemHeight) {
    const key = breakpoint as keyof BreakpointValues;
    const height = itemHeight[key];

    // 1. Guard clause: Skip if the value is undefined or empty
    if (!height) continue;

    const heightValue = parseFloat(height);

    // 2. Extra safety: Skip if parseFloat fails (e.g., invalid string)
    if (isNaN(heightValue)) continue;

    const ratio = aspectRatios[key] || 3 / 2;
    const calculatedWidth = Math.round(heightValue * ratio);

    const varName = `--${key}-width-active` as keyof ItemWidthCSS;
    cssVars[varName] = `${calculatedWidth}px`;
  }

  return cssVars;
}

type ItemInactiveWidthCSS = {
  [K in keyof BreakpointValues as `--${K}-width-inactive`]?: string;
};

/**
 * Calculates the CSS variable expressions for inactive slide widths.
 * @param itemHeight The breakpoint configuration object.
 * @param inactiveCount The number of inactive slides sharing the remaining space (default: 2).
 */
function calculateInactiveWidthsCSS(
  itemHeight: BreakpointValues,
  inactiveCount: number = 2,
): ItemInactiveWidthCSS {
  const cssVars: ItemInactiveWidthCSS = {};

  for (const breakpoint in itemHeight) {
    const key = breakpoint as keyof BreakpointValues;

    // Skip if this breakpoint wasn't provided
    if (!itemHeight[key]) continue;

    const varName = `--${key}-width-inactive` as keyof ItemInactiveWidthCSS;
    const activeVarRef = `var(--${key}-width-active)`;
    const offset = `${key === "mobile" ? inactiveCount / 2 : inactiveCount}rem`;
    // Formula: calc((100% - activeWidth) / quantityOfInactiveSlides)
    cssVars[varName] =
      `calc((100% - ${activeVarRef} - ${offset}) / ${inactiveCount})`;
  }

  return cssVars;
}

const itemHeight = {
  mobile: `240px`,
  tablet: `360px`,
  desktop: `480px`,
  widescreen: `600px`,
  ultrahd: `720px`,
};

export function CustomCarousel({
  elements,
}: {
  elements: InfoTextImageSliderElementType[];
}) {
  const { getIcon } = useGlobalContext();
  const [activeIndex, setActiveIndex] = useState(0);
  const totalSlides = elements.length;

  // jscpd:ignore-start
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;
  // jscpd:ignore-end
  const handlePointerStart = (e: React.PointerEvent<HTMLDivElement>) => {
    // FIX 1: Lock the pointer capture to this element so moves/ups
    // are registered even if the mouse hovers over images or leaves the container.
    e.currentTarget.setPointerCapture(e.pointerId);

    setTouchEnd(null);
    setTouchStart(e.clientX);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (touchStart === null) return;
    setTouchEnd(e.clientX);
  };

  const handlePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (touchStart === null || touchEnd === null) return;

    const distance = touchStart - touchEnd;

    if (distance > minSwipeDistance) {
      // Swiped Left -> Next
      setActiveIndex((prev) => Math.min(prev + 1, totalSlides - 1));
    } else if (distance < -minSwipeDistance) {
      // Swiped Right -> Prev
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    }

    // FIX 2: Release pointer capture and clean up state
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {
      console.error(err);
    }
    setTouchStart(null);
    setTouchEnd(null);
  };
  //   console.log(activeIndex);
  const NAV_JSX = (className: string) => (
    <div
      className={`${styles["navigation"]} ${className} embla-controls only-navigation`}
    >
      <button
        className={`${styles["nav-button"]} ${styles.left} pagination`}
        onClick={() => setActiveIndex((prev) => Math.max(prev - 1, 0))}
        onPointerDown={(e) => e.stopPropagation()}
        aria-disabled={activeIndex === 0}
        disabled={activeIndex === 0}
      >
        <CustomSVG
          className="slider-button-icon"
          src={getIcon("arrowLeft")}
          width="34"
          height="34"
        />
      </button>
      <button
        className={`${styles["nav-button"]} ${styles.right} pagination`}
        onClick={() =>
          setActiveIndex((prev) => Math.min(prev + 1, totalSlides - 1))
        }
        onPointerDown={(e) => e.stopPropagation()}
        aria-disabled={activeIndex === totalSlides - 1}
        disabled={activeIndex === totalSlides - 1}
      >
        <CustomSVG
          className="slider-button-icon"
          src={getIcon("arrowRight")}
          width="34"
          height="34"
        />
      </button>
    </div>
  );

  return (
    <div
      className={styles["image-slider"]}
      onPointerDown={handlePointerStart}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      style={
        {
          touchAction: "pan-y", // Allows vertical page scrolling, but lets JS intercept horizontal swipes
          userSelect: "none", // Stops text selection from highlighting while swiping
          overflow: "hidden", // Keeps everything contained
          width: "100%",
          "--mobile-height": "240px",
          "--tablet-height": "360px",
          "--desktop-height": "480px",
          "--widescreen-height": "600px",
          "--ultrahd-height": "720px",
          ...calculateItemWidthsCSS(itemHeight),
          ...calculateInactiveWidthsCSS(itemHeight, elements.length - 1),
        } as React.CSSProperties
      }
    >
      {NAV_JSX("is-hidden-desktop")}
      <ul
        className={styles["slider-track"]}
        style={{
          display: "flex",
          transition: "transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)",
          width: "100%",
        }}
      >
        {elements.map((el, i) => {
          const { image, bild_mobil, bild_tablet, title, content, link } = el;
          return (
            <li
              key={i}
              className={styles["image-slide"]}
              data-active={i === activeIndex}
            >
              <div
                className={styles["image-slide-content"]}
                onDragStart={(e) => e.preventDefault()}
              >
                {image && (
                  <MediaItem
                    objectFit="cover"
                    image_desktop={image}
                    image_mobile={bild_mobil}
                    image_tablet={bild_tablet}
                    sizes="100vw"
                    infoButtonPosition={"top"}
                  />
                )}
              </div>
              {i === activeIndex && (
                <InfoTextBox title={title} content={content} link={link} />
              )}
            </li>
          );
        })}
      </ul>
      {NAV_JSX("is-hidden-touch")}
    </div>
  );
}
